import React, { useMemo, useRef, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { Box, Button, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { ArrowUpDownIcon } from 'lucide-react';

import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import type { ReportComponentProps } from '@modules/_shared/components/report-editor/ReportEditorContext';
import {
  GET_CMA_AGENT_TRANSACTIONS,
  GET_CMA_AGENT_TRANSACTIONS_PAGINATED,
  UPDATE_CMA_AGENT_SORTING_MODE,
} from '@modules/reports/cma-reports/cmaReportsQueries';
import { AgentTransactionCard } from '@modules/reports/cma-reports/step-agent/AgentTransactionCard';
import { splitLoadedPage } from '@modules/reports/cma-reports/step-agent/loadMorePage';
import type { AgentTransaction } from '@modules/reports/cma-reports/step-agent/types';
import { FooterActionsComponentWithoutForm } from '@modules/reports/shared/FooterActionsComponentWithoutForm';
import { useLocale } from '@shared/hooks/locale';

export type SortMode = 'nearest' | 'recent';

const INITIAL_VISIBLE_COUNT = 8;
const INITIAL_FETCH_COUNT = 15;
const PAGINATION_INCREMENT = 8;
const DEFAULT_SORT_MODE: SortMode = 'nearest';

type SortModeMenuProps = {
  anchorEl: HTMLElement | null;
  sortMode: SortMode;
  onClose: () => void;
  onSortChange: (mode: SortMode) => void;
  t: (key: string) => string;
};

const SortModeMenu = ({
  anchorEl,
  sortMode,
  onClose,
  onSortChange,
  t,
}: SortModeMenuProps) => (
  <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
    <MenuItem
      selected={sortMode === 'nearest'}
      onClick={() => onSortChange('nearest')}
    >
      {t('Nearest transactions')}
    </MenuItem>
    <MenuItem
      selected={sortMode === 'recent'}
      onClick={() => onSortChange('recent')}
    >
      {t('Most recent')}
    </MenuItem>
  </Menu>
);

const StepAgentProfile = ({
  reportId: cmaReportId,
  setStep,
  step,
}: ReportComponentProps) => {
  const { t } = useLocale();
  const client = useApolloClient();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [lastPageHasMore, setLastPageHasMore] = useState<boolean | null>(null);

  const { data: visibleTransactionIdsData } = useQuery(
    GET_CMA_AGENT_TRANSACTIONS,
    {
      variables: { cmaReportId },
      skip: !cmaReportId,
    },
  );

  const [isChangingSort, setIsChangingSort] = useState(false);
  const prefetchedSort = useRef<{
    reportId: string;
    sortMode: SortMode;
  } | null>(null);

  const sortMode = useMemo(
    () =>
      (visibleTransactionIdsData?.cma_reports_by_pk
        ?.sort_display_mode as SortMode) ?? DEFAULT_SORT_MODE,
    [visibleTransactionIdsData?.cma_reports_by_pk?.sort_display_mode],
  );

  const visibleTransactionIds = useMemo(
    () =>
      visibleTransactionIdsData?.cma_reports_by_pk?.cma_agent_transactions?.map(
        tx => tx.transaction_id,
      ) ?? [],
    [visibleTransactionIdsData?.cma_reports_by_pk?.cma_agent_transactions],
  );

  const {
    data: paginatedTransactionsData,
    loading: paginatedTransactionsLoading,
    fetchMore,
  } = useQuery(GET_CMA_AGENT_TRANSACTIONS_PAGINATED, {
    variables: {
      cmaReportId,
      sortMode,
      limit: INITIAL_FETCH_COUNT + 1,
      offset: 0,
    },
    fetchPolicy: 'network-only',
    // Sort changes already fetch and cache the next candidate list before saving.
    nextFetchPolicy: (_fetchPolicy, { reason }) => {
      if (reason !== 'variables-changed') {
        return 'cache-first';
      }
      const prefetched = prefetchedSort.current;
      prefetchedSort.current = null;
      return prefetched?.reportId === cmaReportId &&
        prefetched?.sortMode === sortMode
        ? 'cache-first'
        : 'network-only';
    },
    notifyOnNetworkStatusChange: true,
  });

  const [updateSortingMode, { loading: updatingSortingMode }] = useMutation(
    UPDATE_CMA_AGENT_SORTING_MODE,
    {
      onError: setErrorLike,
      refetchQueries: [GET_CMA_AGENT_TRANSACTIONS],
      awaitRefetchQueries: true,
    },
  );

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const { items: transactions, hasMore } = splitLoadedPage(
    paginatedTransactionsData?.get_cma_agent_transactions ?? [],
    lastPageHasMore,
    INITIAL_FETCH_COUNT,
  );

  const handleLoadMore = async () => {
    if (!cmaReportId || paginatedTransactionsLoading || !hasMore) {
      return;
    }

    const nextOffset = transactions.length;

    await fetchMore({
      variables: {
        offset: nextOffset,
        limit: PAGINATION_INCREMENT + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }

        const newTransactions = fetchMoreResult.get_cma_agent_transactions;

        // The extra row past the page only tells whether more remain
        setLastPageHasMore(newTransactions.length > PAGINATION_INCREMENT);

        return {
          get_cma_agent_transactions: [
            ...(prev.get_cma_agent_transactions ?? []).slice(0, nextOffset),
            ...newTransactions,
          ],
        };
      },
    });
  };

  const handleSortChange = async (newValue: SortMode) => {
    handleMenuClose();

    // Prevent duplicate operations or operations during loading
    if (
      !cmaReportId ||
      newValue === sortMode ||
      updatingSortingMode ||
      paginatedTransactionsLoading ||
      isChangingSort
    ) {
      return;
    }

    setIsChangingSort(true);

    try {
      const { data: allData } = await client.query({
        query: GET_CMA_AGENT_TRANSACTIONS_PAGINATED,
        variables: {
          cmaReportId,
          sortMode: newValue,
          limit: INITIAL_FETCH_COUNT + 1,
          offset: 0,
        },
        fetchPolicy: 'network-only',
      });
      const allTransactionsOrdered = allData?.get_cma_agent_transactions ?? [];
      const transactionsToInsert = allTransactionsOrdered.slice(
        0,
        INITIAL_VISIBLE_COUNT,
      );
      prefetchedSort.current = { reportId: cmaReportId, sortMode: newValue };

      // Publish the sort and replacement selection together, after fetching succeeds.
      await updateSortingMode({
        variables: {
          cmaReportId,
          sortMode: newValue,
          hasTransactions: transactionsToInsert.length > 0,
          objects: transactionsToInsert.map(tx => ({
            cma_report_id: cmaReportId,
            transaction_id: tx.transaction_id,
            distance_in_meters: tx.distance_in_meters,
            zone: tx.zone,
          })),
        },
      });
      setLastPageHasMore(null);
    } catch (error) {
      prefetchedSort.current = null;
      setErrorLike(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setIsChangingSort(false);
    }
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Stack
        sx={{
          gap: 2,
          p: 2,
          flexGrow: 1,
          height: 0,
          overflow: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t('Transactions')}
          </Typography>

          <Button
            onClick={handleMenuOpen}
            startIcon={<ArrowUpDownIcon />}
            sx={{
              color: 'text.primary',
            }}
          >
            {sortMode === 'nearest'
              ? t('Nearest transactions')
              : t('Most recent')}
          </Button>

          <SortModeMenu
            anchorEl={anchorEl}
            sortMode={sortMode}
            onClose={handleMenuClose}
            onSortChange={handleSortChange}
            t={t}
          />
        </Box>

        {(updatingSortingMode || isChangingSort) && <LoadingSpinner />}

        {!updatingSortingMode &&
          !isChangingSort &&
          paginatedTransactionsLoading &&
          transactions.length === 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <LoadingSpinner />
            </Box>
          )}

        {!updatingSortingMode &&
          !isChangingSort &&
          !paginatedTransactionsLoading &&
          transactions.length === 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('No transactions found')}
              </Typography>
            </Box>
          )}

        {!updatingSortingMode && !isChangingSort && transactions.length > 0 && (
          <>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {transactions.map(transaction => {
                const transactionWithVisibility: AgentTransaction = {
                  ...transaction,
                  is_visible: visibleTransactionIds.includes(
                    transaction.transaction_id,
                  ),
                };
                return (
                  <AgentTransactionCard
                    key={transaction.transaction_id}
                    transaction={transactionWithVisibility}
                  />
                );
              })}
            </Stack>

            {hasMore && (
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                loadingPosition="start"
                loading={paginatedTransactionsLoading}
                fullWidth
              >
                {t('Load more')}
              </Button>
            )}
          </>
        )}
      </Stack>
      <FooterActionsComponentWithoutForm
        updating={updatingSortingMode}
        step={step}
        setStep={setStep}
      />
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </Box>
  );
};

export default StepAgentProfile;
